import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { initializeFaro } from '@grafana/faro-web-sdk';
import { useAuthUser } from 'features/auth/AuthUserContext';
import { useRouter } from 'next/router';

const RumContext = createContext(null);

export const useRum = () => {
  const context = useContext(RumContext);
  return context;
};

export const RumProvider = ({ children, grafana, app, env, version }) => {
  const [faroInstance, setFaroInstance] = useState(null);
  const { user, isAuthenticated } = useAuthUser();
  const router = useRouter();

  // Memoized event filter function
  const beforeSend = useCallback((event) => {
    try {
      if (event.type === 'log' && 'level' in event.payload) {
        const level = event.payload.level;
        const message = event.payload?.message || '';

        // Drop translation warnings and other noise
        if (
          level === 'warn' &&
          (message.includes('Missing Translation') || message.includes('Failed prop type'))
        ) {
          return null;
        }
      } else if (event.type === 'exception' && 'level' in event.payload) {
        const level = event.payload.level;
        const message = event.payload?.message || '';

        // Drop known non-critical errors
        const ignoredPatterns = [
          /Position \d+ outside of fragment/,
          /ResizeObserver loop/,
          /Non-Error promise rejection captured/,
        ];

        if (level === 'error' && ignoredPatterns.some((pattern) => pattern.test(message))) {
          return null;
        }
      }
      return event;
    } catch (error) {
      console.error('Rum Machine error: Error in beforeSend filtering:', error);
      return event;
    }
  }, []);

  useEffect(() => {
    const initFaro = async () => {
      // Only initialize in browser environment
      if (typeof window === 'undefined') return;

      // Skip if already initialized
      if (window.faro) {
        setFaroInstance(window.faro);
        return;
      }

      // Skip if required config is missing
      if (!grafana?.host || !grafana?.key) {
        console.warn('Grafana Faro config missing. Skipping initialization.');
        return;
      }

      // Skip in development unless explicitly enabled
      if (env === 'development' && !process.env.NEXT_PUBLIC_ENABLE_FARO) {
        console.log(
          'Grafana Faro disabled in development. Set NEXT_PUBLIC_ENABLE_FARO=true to enable.',
        );
        return;
      }

      try {
        // Get or create session-based anonymous ID (lightweight, no external calls)
        let anonymousId;
        try {
          anonymousId = sessionStorage.getItem('faro_anonymous_id');
          if (!anonymousId) {
            anonymousId = `${Math.random().toString(36).substring(2, 15)}_${Date.now()}`;
            sessionStorage.setItem('faro_anonymous_id', anonymousId);
          }
        } catch {
          anonymousId = Math.random().toString(36).substring(2, 15);
        }

        const faro = initializeFaro({
          url: grafana.host,
          apiKey: grafana.key,
          app: {
            name: app || 'mtg-frontend',
            version: version || '1.0.0',
            environment: env || 'development',
          },
          batching: {
            enabled: true,
            itemLimit: 100,
            sendTimeout: 60000,
          },
          sessionTracking: {
            enabled: true,
            persistent: true,
            samplingRate: 1.0, // Track 100% of sessions, adjust for high traffic (e.g., 0.1 = 10%)
          },
          beforeSend,
          user: {
            email: isAuthenticated && user?.email ? user.email : undefined,
            attributes: {
              anonymousId,
              userId: isAuthenticated && user?.id ? String(user.id) : undefined,
            },
          },
        });

        setFaroInstance(faro);

        // Expose Faro API to window for easy access
        if (window.faro) {
          window.faroAPI = {
            pushError: window.faro.api.pushError,
            pushMeasurement: window.faro.api.pushMeasurement,
            getSession: window.faro.api.getSession,
            setSession: window.faro.api.setSession,
            setUser: window.faro.api.setUser,
          };
        }
      } catch (error) {
        console.error('Failed to initialize Grafana Faro:', error);
      }
    };

    initFaro();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [beforeSend]);

  // Page tracking - track route changes in Next.js
  useEffect(() => {
    if (!faroInstance || !window.faro) return;

    const handleRouteChange = (url) => {
      try {
        window.faro.api.pushEvent('route_change', {
          url,
          referrer: document.referrer,
        });
      } catch (error) {
        console.error('Failed to track route change:', error);
      }
    };

    router.events.on('routeChangeComplete', handleRouteChange);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [faroInstance, router.events]);

  // Update user info when authentication state changes
  useEffect(() => {
    if (!faroInstance || !window.faro || !isAuthenticated || !user?.email) return;

    try {
      window.faro.api.setUser({
        email: user.email,
        attributes: {
          userId: String(user.id),
          anonymousId: sessionStorage.getItem('faro_anonymous_id') || '',
        },
      });
    } catch (error) {
      console.error('Failed to update Faro user:', error);
    }
  }, [faroInstance, isAuthenticated, user?.email, user?.id]);

  return <RumContext.Provider value={faroInstance}>{children}</RumContext.Provider>;
};
